import {
  formatMoney,
  type MoneyContext,
  type MoneyRounding,
  rateCurrency,
} from "@reactive-resume/utils";
import { useQuery } from "@tanstack/react-query";
import { useCallback, useMemo } from "react";

import { countries, countryNotProvided } from "@/client/constants/country-preferences";
import { axios } from "@/client/libs/axios";
import { usePaymentOptions } from "@/client/services/payment/payment-options";
import { useAuthStore } from "@/client/stores/auth";

type DetectedCurrency = {
  currency_code: string | null;
  country_code: string | null;
  locale: string | null;
  rate: number | null;
};

const getDetectedCurrency = async () => {
  const response = await axios.get<DetectedCurrency>("/currency");

  return response.data;
};

// Pays déduit de l'adresse IP : sert uniquement à pré-remplir le choix du pays.
export const useDetectedCountry = (enabled = true) => {
  const { data } = useQuery({
    queryKey: ["detected-currency"],
    queryFn: getDetectedCurrency,
    enabled,
    staleTime: Infinity,
  });

  return data?.country_code ?? null;
};

export const getAccountLocale = (countryCode?: string | null) =>
  countries.find((country) => country.countryCode === countryCode)?.locale ??
  countryNotProvided.locale;

// Même table de taux que le serveur au moment du débit par carte.
export const getCardMoneyContext = (currency: string, locale: string): MoneyContext | null => {
  const rate = rateCurrency.find((entry) => currency in entry)?.[currency];

  return rate ? { currency, rate, locale } : null;
};

// Devise du pays du compte, pour tout montant stocké en FCFA.
export const useAccountCurrency = () => {
  const user = useAuthStore((state) => state.user);
  const hasCountry = Boolean(user?.countryPreferenceSetAt);
  const { options, error } = usePaymentOptions(hasCountry);

  // Aucune devise de repli : sans pays ou sans options, aucun montant n'est affiché.
  const context = useMemo<MoneyContext | null>(() => {
    if (!hasCountry || !options) return null;

    return {
      currency: options.displayCurrencyCode,
      rate: options.displayRate,
      locale: getAccountLocale(user?.countryCode),
    };
  }, [hasCountry, options, user?.countryCode]);

  const format = useCallback(
    (amountFcfa: number, rounding?: MoneyRounding) => {
      if (context) return formatMoney(amountFcfa, context, rounding);

      return error ? "—" : "…";
    },
    [context, error],
  );

  return { context, options, format };
};
